// src/pages/grades/ParentGrades.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';
import { FireLoader } from '../../components/Loader';

// Helper to format rank
const formatRank = (rank) => {
  if (rank === null || rank === undefined || rank === '-') return '-';
  if (rank === 1) return '1st';
  if (rank === 2) return '2nd';
  if (rank === 3) return '3rd';
  return `${rank}th`;
};

// Helper to conditionally color values below 50
const getColorClass = (value) => {
  if (value === null || value === undefined || value === '-') return '';
  const num = Number(value);
  if (!isNaN(num) && num < 50) return 'text-red-500 font-semibold';
  return '';
};

// Helper: format term label
const formatTerm = (term) => term.replace('term_', 'Term ');

// Helper: grade letter (kept for PDF only)
const getGradeLetter = (score) => {
  if (score === null || score === undefined) return '-';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

export default function ParentGrades() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [gradeData, setGradeData] = useState([]);
  const [academicYear, setAcademicYear] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/students/parent/children?_t=${Date.now()}`);
      if (!res.data.success) {
        toast.error('Could not fetch your children.');
        setChildren([]);
        setLoading(false);
        return;
      }
      const childrenData = res.data.data || [];
      setChildren(childrenData);

      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
        await fetchGrades(childrenData[0].id);
      }
    } catch (error) {
      console.error('Error fetching children:', error);
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const fetchGrades = async (childId) => {
    try {
      const res = await apiClient.get(`/students/${childId}/grades?_t=${Date.now()}`);
      if (res.data.success) {
        setGradeData(res.data.data || []);
        setAcademicYear(res.data.academic_year || '');
      } else {
        setGradeData([]);
        setAcademicYear('');
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      toast.error('Failed to load grades');
      setGradeData([]);
    }
  };

  const handleChildChange = (e) => {
    const childId = parseInt(e.target.value);
    const child = children.find(c => c.id === childId);
    if (child) {
      setSelectedChild(child);
      fetchGrades(child.id);
    }
  };

  const goBack = () => navigate('/app/dashboard');

  // Compute totals per term and yearly (for summary rows)
  const computeTotals = () => {
    const termTotals = { term_1: 0, term_2: 0, term_3: 0 };
    let yearlyTotal = 0;
    let count = 0;
    gradeData.forEach((item) => {
      const terms = item.terms || {};
      ['term_1', 'term_2', 'term_3'].forEach((key) => {
        if (terms[key]?.mean) termTotals[key] += terms[key].mean;
      });
      if (item.yearly?.mean) yearlyTotal += item.yearly.mean;
      count++;
    });
    if (count > 0) {
      yearlyTotal = Math.round(yearlyTotal);
      Object.keys(termTotals).forEach((key) => {
        termTotals[key] = Math.round(termTotals[key]);
      });
    }
    const numSubjects = gradeData.length || 1;
    const termAverages = {
      term_1: Math.round(termTotals.term_1 / numSubjects),
      term_2: Math.round(termTotals.term_2 / numSubjects),
      term_3: Math.round(termTotals.term_3 / numSubjects),
    };
    const yearlyAvg = Math.round(yearlyTotal / numSubjects);
    return { termTotals, yearlyTotal, termAverages, yearlyAvg };
  };

  const { termTotals, yearlyTotal, termAverages, yearlyAvg } = computeTotals();

  // ==================== PDF GENERATION ====================
  const generatePrintHTML = () => {
    if (!selectedChild) return '';
    const fullName = `${selectedChild.first_name} ${selectedChild.last_name}`;
    const classInfo = selectedChild.class?.name || 'Not Assigned';
    const admission = selectedChild.admission_number || 'N/A';
    const year = academicYear || '';

    let subjectRows = '';
    gradeData.forEach((item) => {
      const terms = item.terms || {};
      const yearly = item.yearly || {};
      const t1 = terms.term_1?.mean ?? '-';
      const t2 = terms.term_2?.mean ?? '-';
      const t3 = terms.term_3?.mean ?? '-';
      const yearlyMean = yearly.mean ?? '-';
      const grade = getGradeLetter(yearlyMean);

      subjectRows += `
        <tr>
          <td style="padding: 4px 8px; font-size: 11px; border: 1px solid #ccc; font-weight: 600;">${item.subject}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t1}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t2}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t3}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearlyMean}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${grade}</td>
        </tr>
      `;
    });

    const totalsRow = `
      <tr style="background: #e8f0fe;">
        <td style="padding: 4px 8px; font-weight: bold; border: 1px solid #ccc;">Combine</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_1 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_2 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_3 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearlyTotal}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">-</td>
      </tr>
    `;
    const avgRow = `
      <tr style="background: #f0f4ff;">
        <td style="padding: 4px 8px; font-weight: bold; border: 1px solid #ccc;">Average</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_1}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_2}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_3}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearlyAvg}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${getGradeLetter(yearlyAvg)}</td>
      </tr>
    `;

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Report Card - ${fullName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', Times, serif;
              padding: 20px 30px;
              color: #1a1a1a;
              background: white;
              font-size: 12px;
            }
            @page {
              size: A4 landscape;
              margin: 15mm 18mm;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c;
              padding-bottom: 12px;
              margin-bottom: 18px;
            }
            .print-header-left h1 {
              font-size: 22px;
              color: #1a3a5c;
              font-weight: bold;
              letter-spacing: 1px;
            }
            .print-header-left p {
              color: #555;
              font-size: 11px;
              margin-top: 2px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-title {
              text-align: center;
              font-size: 18px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 15px;
              letter-spacing: 1.5px;
            }
            .student-info {
              display: flex;
              justify-content: space-between;
              background: #f8f9fa;
              padding: 8px 15px;
              border: 1px solid #ddd;
              border-radius: 4px;
              margin-bottom: 18px;
              font-size: 12px;
            }
            .student-info span { font-weight: bold; }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
            }
            th {
              background: #e9ecef;
              border: 1px solid #ccc;
              padding: 6px 8px;
              text-align: center;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
            }
            td {
              border: 1px solid #ccc;
              padding: 4px 8px;
              font-size: 11px;
            }
            .section-title {
              font-size: 14px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 6px;
            }
            .summary-box {
              display: flex;
              justify-content: space-between;
              background: #f8f9fa;
              border: 1px solid #ddd;
              border-radius: 4px;
              padding: 8px 15px;
              margin-top: 12px;
              font-size: 12px;
            }
            .summary-box span { font-weight: bold; }
            .print-footer {
              text-align: center;
              margin-top: 20px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              color: #888;
              font-size: 9px;
            }
            .print-watermark {
              position: fixed;
              bottom: 20px;
              right: 20px;
              opacity: 0.08;
              font-size: 45px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 15px 20px; }
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">PAWA SCHOOL</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>PAWASCHOOL</h1>
              <p>Student Report Card</p>
            </div>
            <div class="print-header-right">
              <p>Generated: ${new Date().toLocaleDateString()}</p>
              <p>Time: ${new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div class="print-title">ACADEMIC PERFORMANCE REPORT</div>

          <div class="student-info">
            <div><span>Student:</span> ${fullName}</div>
            <div><span>Class:</span> ${classInfo}</div>
            <div><span>Admission:</span> ${admission}</div>
            <div><span>Academic Year:</span> ${year}</div>
            <div><span>Subjects:</span> ${gradeData.length}</div>
          </div>

          <div class="section-title">Subject Scores</div>
          <table>
            <thead>
              <tr>
                <th style="width:20%;">Subject</th>
                <th style="width:14%;">Term 1</th>
                <th style="width:14%;">Term 2</th>
                <th style="width:14%;">Term 3</th>
                <th style="width:16%;">Yearly Avg</th>
                <th style="width:12%;">Grade</th>
              </tr>
            </thead>
            <tbody>
              ${subjectRows}
              ${totalsRow}
              ${avgRow}
            </tbody>
          </table>

          <div class="summary-box">
            <div><span>Overall Average:</span> ${yearlyAvg}%</div>
            <div><span>Total Combined:</span> ${yearlyTotal}</div>
            <div><span>Subjects Passed:</span> ${gradeData.filter(s => (s.yearly?.mean || 0) >= 50).length} / ${gradeData.length}</div>
            <div><span>Grade:</span> ${getGradeLetter(yearlyAvg)}</div>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>PawaSchool Management System | All Rights Reserved</p>
          </div>
        </body>
      </html>
    `;
  };

  const handleDownloadPDF = async () => {
    if (!selectedChild) {
      toast.error('No child selected.');
      return;
    }
    if (gradeData.length === 0) {
      toast.error('No grades available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(generatePrintHTML());
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  if (children.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">No Children Linked</h3>
          <p className="text-gray-400 mt-2">
            You don't have any children linked to your account.
          </p>
          <button
            onClick={goBack}
            className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Child's Grades</h1>
            <p className="text-sm text-gray-500">View your child's academic performance</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Child Selector */}
          <div className="relative w-full sm:w-64">
            <select
              value={selectedChild?.id || ''}
              onChange={handleChildChange}
              className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              {children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.first_name} {child.last_name} ({child.class?.name || 'No Class'})
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Download Button */}
          {selectedChild && gradeData.length > 0 && (
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              {isDownloading ? 'Preparing...' : 'Download Results'}
            </button>
          )}
        </div>
      </div>

      {selectedChild && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Student info row: name on left, class + academic year on right */}
          {selectedChild && (
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-semibold text-gray-800">
                  {selectedChild.first_name} {selectedChild.last_name}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span>
                  Class: <span className="font-medium text-gray-800">{selectedChild.class?.name || 'Not Assigned'}</span>
                </span>
                {academicYear && (
                  <span>
                    Academic Year: <span className="font-medium text-gray-800">{academicYear}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Grade Table – same as student page (no grade letter column) */}
          {gradeData.length > 0 ? (
            <div className="overflow-x-auto p-4">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 rounded-tl-lg border border-gray-200">
                      Subject
                    </th>
                    {['Term 1', 'Term 2', 'Term 3'].map((term) => (
                      <th key={term} className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="3">
                        {term}
                      </th>
                    ))}
                    <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="2">
                      Yearly
                    </th>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-2 text-xs text-gray-400 font-medium border border-gray-200"></th>
                    {['Test1', 'Test2', 'Mean', 'Test1', 'Test2', 'Mean', 'Test1', 'Test2', 'Mean'].map((label, i) => (
                      <th key={i} className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">
                        {label}
                      </th>
                    ))}
                    <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Mean</th>
                    <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Rank</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeData.map((item, index) => {
                    const terms = item.terms || {};
                    const yearly = item.yearly || {};
                    return (
                      <tr key={index} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-800 border border-gray-100">
                          {item.subject}
                          {item.subject_code && (
                            <span className="text-xs text-gray-400 ml-2">({item.subject_code})</span>
                          )}
                        </td>
                        {['term_1', 'term_2', 'term_3'].map((termKey) => {
                          const term = terms[termKey] || {};
                          return (
                            <React.Fragment key={termKey}>
                              <td className={`px-2 py-3 text-center border border-gray-100 ${getColorClass(term.test1)}`}>
                                {term.test1 ?? '-'}
                              </td>
                              <td className={`px-2 py-3 text-center border border-gray-100 ${getColorClass(term.test2)}`}>
                                {term.test2 ?? '-'}
                              </td>
                              <td className={`px-2 py-3 text-center font-semibold border border-gray-100 ${getColorClass(term.mean)}`}>
                                {term.mean ?? '-'}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className={`px-2 py-3 text-center font-bold border border-gray-100 ${getColorClass(yearly.mean)}`}>
                          {yearly.mean ?? '-'}
                        </td>
                        <td className="px-2 py-3 text-center text-sm text-gray-500 border border-gray-100">
                          {formatRank(yearly.rank)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* ===== COMBINE OF ALL SUBJECTS ROW ===== */}
                  <tr className="bg-gray-50/70 border-t border-gray-200">
                    <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">
                      Combine of all Subjects
                    </td>
                    {['term_1', 'term_2', 'term_3'].map((termKey) => (
                      <React.Fragment key={termKey}>
                        <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                          -
                        </td>
                        <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                          -
                        </td>
                        <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                          {termTotals[termKey] || 0}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                      {yearlyTotal}
                    </td>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                  </tr>

                  {/* ===== AVERAGE ROW ===== */}
                  <tr className="bg-gray-50/50 border-t border-gray-200">
                    <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">
                      Average
                    </td>
                    {['term_1', 'term_2', 'term_3'].map((termKey) => (
                      <React.Fragment key={termKey}>
                        <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                          -
                        </td>
                        <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                          -
                        </td>
                        <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                          {termAverages[termKey] || 0}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                      {yearlyAvg}
                    </td>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No Grades Recorded</h3>
              <p className="text-gray-400 mt-1">
                Grades will appear here once teachers start recording assessments.
              </p>
            </div>
          )}

          {/* Footer with Mean definition */}
          <div className="p-4 border-t border-gray-200 bg-gray-50/50 text-xs text-gray-400 flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-gray-500">Mean = average of Test1 and Test2</span>
            <span>Rank based on class performance</span>
          </div>
        </div>
      )}
    </div>
  );
}